import { useCallback, useMemo } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import { AcceptSubmissionModal, useGlobalState } from '@pepper/ui'
import { useIsMutating } from '@tanstack/react-query'
import { Icon } from '@iconify/react'

import DeleteItemModal from '../DeleteModal/DeleteItemModal'
import { CLIENT_STAGES } from '@pepper/utils/src/constant/enum/assignment_stage'

import { useGetWorkflowOnOffState } from 'utils/hooks/workflows/useGetWorkflowToggledState'
import BulkReworkModal from 'container/Projects/LiveProjects/ProjectDetails/AssignmentTab/BulkReworkModal'
import AssigneeDropdown from 'components/workflows/AssigneeDropdown'
import StatusDropdown from 'components/workflows/StatusDropdown'
import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'

type Props = {
  items: Assignment[]
  selectedItems: Assignment[]
  selectedItemsStage: AssignmentStage | null | ''
  companyId: string
  setSelectedItems: (items: Assignment[]) => void
  onDownload: (items: Assignment[]) => void
  refetchRecords: () => void
  assigneeList?: TeamMembers[]
  workflowStatusList?: WorkflowStatus[]
  handleDocsDownload?: (items: Assignment[]) => void
}

const OrdersBottomPopup = ({
  items,
  selectedItems,
  selectedItemsStage,
  companyId,
  setSelectedItems,
  onDownload,
  refetchRecords,
  handleDocsDownload,
  assigneeList,
  workflowStatusList,
}: Props) => {
  const { bulkDownloadJob } = useGlobalState()
  const isMutatingBulkDownloads = useIsMutating({
    mutationKey: ['bulk-export'],
  })
  const { show: showDeleteModal } = useModal(DeleteItemModal)
  const { show: showBulkReworkModal } = useModal(BulkReworkModal)
  const { show: showAcceptItemModal } = useModal(AcceptSubmissionModal)
  const { data: toggleState } = useGetWorkflowOnOffState({ companyId })

  const currentCommonStatus =
    selectedItemsStage && CLIENT_STAGES[selectedItemsStage]?.title
  const deletable = selectedItems.every(
    item => item.item_type === 'private_doc'
  )

  const showReworkAndApprove = selectedItemsStage === 'finished'

  const selectAllItemsText = () => {
    if (currentCommonStatus) {
      return `Select all (${currentCommonStatus})`
    } else {
      return 'Select all'
    }
  }

  const handleClear = () => {
    setSelectedItems([])
  }

  const handleDelete = () => {
    showDeleteModal({
      items: selectedItems,
      onSuccess: refetchRecords,
      onClose: handleClear,
    })
  }

  const handleDownloadItems = () => {
    onDownload(selectedItems)
  }

  const handleSelectAllItems = useCallback(() => {
    if (selectedItemsStage) {
      const _items = items.filter(item => item.stage === selectedItemsStage)
      setSelectedItems(_items)
    } else {
      setSelectedItems(items)
    }
  }, [items, selectedItemsStage])

  const handleBulkApprove = () => {
    showAcceptItemModal({
      assignmentIds: selectedItems.map(item => item.id),
      shouldSubmitAssignmentApproval: true,
      companyId,
      refetch: () => {
        handleClear()
        refetchRecords()
      },
    })
  }

  const handleBulkRework = () => {
    showBulkReworkModal({
      assignments: selectedItems,
      refetch: () => {
        handleClear()
        refetchRecords()
      },
    })
  }

  const downloadableDocs = useMemo(() => {
    const _items = selectedItems.filter(item => {
      if (
        item.category.key === 'text' &&
        item.stage === 'approved' &&
        item.submission_file?.src_type === 'pepper_editor'
      )
        return true
      return false
    })
    return _items
  }, [selectedItems])

  return (
    <BottomPopup
      infoMessage="You can only select items with same statuses"
      selectedCount={selectedItems.length}
      onClose={handleClear}
    >
      <BottomPopupBtn
        onClick={handleSelectAllItems}
        data-id="business-assignments-select-all-button"
      >
        {selectAllItemsText()}
      </BottomPopupBtn>
      {showReworkAndApprove && (
        <>
          <BottomPopupBtn
            onClick={handleBulkApprove}
            icon={<Icon icon="fe:check-circle-o" width={14} />}
          >
            Approve
          </BottomPopupBtn>
          <BottomPopupBtn
            onClick={handleBulkRework}
            icon={<Icon icon="tabler:refresh" width={14} />}
          >
            Rework
          </BottomPopupBtn>
        </>
      )}

      {toggleState?.data.workflow_enabled === 1 && (
        <>
          <AssigneeDropdown
            companyId={companyId}
            assigneeList={assigneeList}
            bulkItems={selectedItems}
            isPrivateDoc={deletable}
            showTooltip={true}
            showSelected={false}
          >
            <BottomPopupBtn
              iconPlacement="right"
              icon={<Icon icon="lucide:chevron-down" />}
            >
              Select Assignee
            </BottomPopupBtn>
          </AssigneeDropdown>

          <StatusDropdown
            companyId={companyId}
            statusList={workflowStatusList}
            bulkItems={selectedItems}
            isPrivateDoc={deletable}
          >
            <BottomPopupBtn
              iconPlacement="right"
              icon={<Icon icon="lucide:chevron-down" />}
            >
              Select Status
            </BottomPopupBtn>
          </StatusDropdown>
        </>
      )}

      <BottomPopupBtn
        onClick={handleDownloadItems}
        data-id="business-assignments-download-button"
        icon={<Icon icon="fe:download" width={14} />}
      >
        Export Docs Links
      </BottomPopupBtn>

      {handleDocsDownload && downloadableDocs.length > 0 && (
        <BottomPopupBtn
          onClick={() => handleDocsDownload(downloadableDocs)}
          disabled={
            bulkDownloadJob?.status === 'pending' || isMutatingBulkDownloads > 0
          }
          icon={<Icon icon="uil:file-download" width="14" />}
        >
          Download Docs
        </BottomPopupBtn>
      )}
      {deletable && <BottomPopupBtn type="delete" onClick={handleDelete} />}
    </BottomPopup>
  )
}

export default OrdersBottomPopup
